// VendorIntakeDialog.jsx - WalletDialog UI Format
import React, { useState, useCallback, useMemo, memo } from "react";
import {
  Dialog,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Grid,
  IconButton,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Paper,
  Stack,
  Avatar,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  AlertTitle,
  Collapse,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Description as DescriptionIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  Language as LanguageIcon,
  Stars as StarsIcon,
} from "@mui/icons-material";
import { useMutation } from "react-query";
import client from './../../API';
import { parseLocalFloat, parseLocalInt } from "../../Utils/constants";
import MuiPhoneInput from "../../Components/MuiPhoneInput";

// TikTok Icon Component
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// ═══════════════════════════════════════════════════════════════════
// MEMOIZED PREVIEW CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════
const VendorPreviewCard = memo(({ 
  creatorName,
  creatorRegion,
  contactEmail,
  phoneNumber,
  notesContentStyle,
  instagramFollowerCount,
  tikTokFollowerCount,
  youtubeFollowerCount,
  completionPercentage,
  isFormValid,
}) => {
  const theme = useTheme();
  
  const totalFollowers = useMemo(() => {
    const ig = parseLocalInt(instagramFollowerCount) || 0;
    const tt = parseLocalInt(tikTokFollowerCount) || 0;
    const yt = parseLocalInt(youtubeFollowerCount) || 0;
    return ig + tt + yt;
  }, [instagramFollowerCount, tikTokFollowerCount, youtubeFollowerCount]);

  const formatFollowers = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const socialPlatforms = useMemo(() => {
    const platforms = [];
    if (instagramFollowerCount) platforms.push({ name: 'Instagram', count: parseLocalInt(instagramFollowerCount), icon: <InstagramIcon /> });
    if (tikTokFollowerCount) platforms.push({ name: 'TikTok', count: parseLocalInt(tikTokFollowerCount), icon: <TikTokIcon /> });
    if (youtubeFollowerCount) platforms.push({ name: 'YouTube', count: parseLocalInt(youtubeFollowerCount), icon: <YouTubeIcon /> });
    return platforms;
  }, [instagramFollowerCount, tikTokFollowerCount, youtubeFollowerCount]);

  return (
    <Card
      elevation={3}
      sx={{
        overflow: 'hidden',
        position: 'sticky',
        top: 16,
      }}
    >
      {/* Header with Gradient */}
      <Box
        sx={{
          height: 140,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
            fontSize: '2.5rem',
          }}
        >
          <BusinessIcon sx={{ fontSize: 48 }} />
        </Avatar>
      </Box>

      <CardContent sx={{ pb: 3 }}>
        <Stack spacing={2.5}>
          {/* Vendor Name */}
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {creatorName || 'Vendor Profile'}
            </Typography>
            {creatorRegion && (
              <Chip 
                icon={<LanguageIcon />}
                label={creatorRegion === 'NACAUKAUS' ? 'USA/Canada/UK/AUS' : creatorRegion}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          <Divider />

          {/* Contact Information */}
          {(contactEmail || phoneNumber) && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Contact Information
              </Typography>
              <Stack spacing={1}>
                {contactEmail && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2">{contactEmail}</Typography>
                  </Box>
                )}
                {phoneNumber && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{phoneNumber}</Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {/* Bio Preview */}
          {notesContentStyle && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Bio
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {notesContentStyle}
              </Typography>
            </Box>
          )}

          {/* Social Media Reach */}
          {totalFollowers > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Total Reach
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <StarsIcon color="primary" />
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  {formatFollowers(totalFollowers)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  followers
                </Typography>
              </Box>
              
              {socialPlatforms.length > 0 && (
                <Stack spacing={0.5}>
                  {socialPlatforms.map((platform) => (
                    <Box 
                      key={platform.name}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        py: 0.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {React.cloneElement(platform.icon, { fontSize: 'small', color: 'action' })}
                        <Typography variant="body2">{platform.name}</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {formatFollowers(platform.count)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )}

          <Divider />

          {/* Completion Progress */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Profile Completion
              </Typography>
              <Typography 
                variant="caption" 
                fontWeight={600} 
                color={isFormValid ? "success.main" : "primary"}
              >
                {completionPercentage}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              color={isFormValid ? "success" : "primary"}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            />
          </Box>

          {/* Status Alert */}
          {isFormValid ? (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <Typography variant="caption">
                ✓ Ready to submit
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="caption">
                Complete required fields to continue
              </Typography>
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.creatorName === nextProps.creatorName &&
    prevProps.creatorRegion === nextProps.creatorRegion &&
    prevProps.contactEmail === nextProps.contactEmail &&
    prevProps.phoneNumber === nextProps.phoneNumber &&
    prevProps.notesContentStyle === nextProps.notesContentStyle &&
    prevProps.instagramFollowerCount === nextProps.instagramFollowerCount &&
    prevProps.tikTokFollowerCount === nextProps.tikTokFollowerCount &&
    prevProps.youtubeFollowerCount === nextProps.youtubeFollowerCount &&
    prevProps.completionPercentage === nextProps.completionPercentage &&
    prevProps.isFormValid === nextProps.isFormValid
  );
});

VendorPreviewCard.displayName = 'VendorPreviewCard';

// ═══════════════════════════════════════════════════════════════════
// COLLAPSIBLE SECTION COMPONENT
// ═══════════════════════════════════════════════════════════════════
const CollapsibleSection = memo(({ title, icon, children, defaultExpanded = true, isMobile }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: isMobile ? 1.5 : 2,
          bgcolor: 'grey.50',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'grey.100' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {React.cloneElement(icon, { 
            sx: { fontSize: isMobile ? 20 : 24, color: 'primary.main' } 
          })}
          <Typography 
            variant={isMobile ? "body1" : "subtitle1"} 
            fontWeight={600}
          >
            {title}
          </Typography>
        </Box>
        {expanded ? 
          <ExpandLessIcon sx={{ fontSize: isMobile ? 20 : 24 }} /> : 
          <ExpandMoreIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
        }
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: isMobile ? 2 : 2.5 }}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
});

CollapsibleSection.displayName = 'CollapsibleSection';

// ═══════════════════════════════════════════════════════════════════
// MEMOIZED FORM CONTENT COMPONENT
// ═══════════════════════════════════════════════════════════════════
const VendorFormContent = memo(({
  formData,
  handleInputChange,
  handleSelectChange,
  handleEmailSameChange,
  isMobile,
}) => {
  return (
    <>
      {/* Basic Information */}
      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 2 : 2.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          mb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <PersonIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Basic Information
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Vendor Name"
              fullWidth
              required
              value={formData.creatorName}
              onChange={handleInputChange("creatorName")}
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required variant="outlined" size={isMobile ? "small" : "medium"}>
              <InputLabel>Vendor Region</InputLabel>
              <Select
                label="Vendor Region"
                value={formData.creatorRegion}
                onChange={handleSelectChange("creatorRegion")}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="NACAUKAUS">USA, Canada, UK, Australia</MenuItem>
                <MenuItem value="Europe">Europe</MenuItem>
                <MenuItem value="Asia">Asia</MenuItem>
                <MenuItem value="LATAM">LATAM</MenuItem>
                <MenuItem value="Africa">Africa</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Bio"
              fullWidth
              multiline
              rows={isMobile ? 3 : 4}
              placeholder="Tell us about yourself and your expertise..."
              value={formData.notesContentStyle}
              onChange={handleInputChange("notesContentStyle")}
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Contact Information */}
      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 2 : 2.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          mb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <EmailIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Contact Information
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contact Email"
              type="email"
              fullWidth
              required
              value={formData.contactEmail}
              onChange={handleInputChange("contactEmail")}
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="PayPal Payment Email"
              type="email"
              fullWidth
              required
              disabled={formData.emailSame}
              value={formData.paymentEmail}
              onChange={handleInputChange("paymentEmail")}
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12}>
            <MuiPhoneInput
              country={'us'}
              preferredCountries={['us']}
              value={formData.phoneNumber}
              onChange={(value) => handleInputChange("phoneNumber")({ target: { value } })}
              isValid={(value) => value.length >= 11}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.emailSame}
                  onChange={handleEmailSameChange}
                  size={isMobile ? "small" : "medium"}
                />
              }
              label={
                <Typography variant="body2">
                  Payment and contact email are the same
                </Typography>
              }
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Social Media Profiles */}
      <Box sx={{ mb: 2 }}>
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            Social media profiles are optional but help showcase your reach and engagement.
          </Typography>
        </Alert>

        {/* Instagram */}
        <CollapsibleSection
          title="Instagram (Optional)"
          icon={<InstagramIcon />}
          defaultExpanded={false}
          isMobile={isMobile}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Instagram Handle"
                fullWidth
                value={formData.instagramLink}
                onChange={handleInputChange("instagramLink")}
                placeholder="username"
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>@</Typography>
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Followers"
                fullWidth
                value={formData.instagramFollowerCount}
                onChange={handleInputChange("instagramFollowerCount")}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                type="number"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Brand Rate"
                fullWidth
                value={formData.instagramBrandRate}
                onChange={handleInputChange("instagramBrandRate")}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                type="number"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Song Rate"
                fullWidth
                value={formData.instagramSongRate}
                onChange={handleInputChange("instagramSongRate")}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                type="number"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </CollapsibleSection>

        {/* TikTok */}
        <CollapsibleSection
          title="TikTok (Optional)"
          icon={<TikTokIcon />}
          defaultExpanded={false}
          isMobile={isMobile}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="TikTok Handle"
                fullWidth
                value={formData.tikTokLink}
                onChange={handleInputChange("tikTokLink")}
                placeholder="username"
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>@</Typography>
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Followers"
                fullWidth
                value={formData.tikTokFollowerCount}
                onChange={handleInputChange("tikTokFollowerCount")}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                type="number"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Brand Rate"
                fullWidth
                value={formData.tikTokBrandRate}
                onChange={handleInputChange("tikTokBrandRate")}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                type="number"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Song Rate"
                fullWidth
                value={formData.tikTokSongRate}
                onChange={handleInputChange("tikTokSongRate")}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                type="number"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </CollapsibleSection>

        {/* YouTube */}
        <CollapsibleSection
          title="YouTube (Optional)"
          icon={<YouTubeIcon />}
          defaultExpanded={false}
          isMobile={isMobile}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="YouTube Handle"
                fullWidth
                value={formData.youtubeLink}
                onChange={handleInputChange("youtubeLink")}
                placeholder="@username"
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Subscribers"
                fullWidth
                value={formData.youtubeFollowerCount}
                onChange={handleInputChange("youtubeFollowerCount")}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                type="number"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Shorts Rate"
                fullWidth
                value={formData.youtubeShortsBrandRate}
                onChange={handleInputChange("youtubeShortsBrandRate")}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                type="number"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </CollapsibleSection>
      </Box>
    </>
  );
});

VendorFormContent.displayName = 'VendorFormContent';

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const VendorIntakeDialog = memo(({ open, onClose, onSubmit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // AppShell spacing constant
  const APPSHELL_HEIGHT = 86;

  // Form State
  const [contactEmail, setContactEmail] = useState("");
  const [paymentEmail, setPaymentEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [emailSame, setEmailSame] = useState(false);
  const [creatorName, setCreatorName] = useState("");
  const [creatorRegion, setCreatorRegion] = useState("");
  const [primaryMarket, setPrimaryMarket] = useState([]);
  const [notesContentStyle, setNotesContentStyle] = useState("");
  
  // Unused state variables kept for API compatibility
  const [race, setRace] = useState("");
  const [gender, setGender] = useState("");
  const [location, setLocation] = useState("");
  const [pfphref, setPfphref] = useState("");

  // Social Media State
  const [instagramLink, setInstagramLink] = useState("");
  const [instagramBrandRate, setInstagramBrandRate] = useState("");
  const [instagramFollowerCount, setInstagramFollowerCount] = useState("");
  const [instagramSongRate, setInstagramSongRate] = useState("");
  const [instagramStoryRate, setInstagramStoryRate] = useState("");

  const [tikTokLink, setTikTokLink] = useState("");
  const [tikTokFollowerCount, setTikTokFollowerCount] = useState("");
  const [tikTokBrandRate, setTikTokBrandRate] = useState("");
  const [tikTokSongRate, setTikTokSongRate] = useState("");

  const [youtubeLink, setYoutubeLink] = useState("");
  const [youtubeFollowerCount, setYoutubeFollowerCount] = useState("");
  const [youtubeShortsBrandRate, setYoutubeShortsBrandRate] = useState("");

  // Memoize form data object
  const formData = useMemo(() => ({
    contactEmail,
    paymentEmail,
    phoneNumber,
    emailSame,
    creatorName,
    creatorRegion,
    notesContentStyle,
    instagramLink,
    instagramBrandRate,
    instagramFollowerCount,
    instagramSongRate,
    instagramStoryRate,
    tikTokLink,
    tikTokFollowerCount,
    tikTokBrandRate,
    tikTokSongRate,
    youtubeLink,
    youtubeFollowerCount,
    youtubeShortsBrandRate,
  }), [
    contactEmail, paymentEmail, phoneNumber, emailSame, creatorName, creatorRegion,
    notesContentStyle, instagramLink, instagramBrandRate, instagramFollowerCount,
    instagramSongRate, instagramStoryRate, tikTokLink, tikTokFollowerCount,
    tikTokBrandRate, tikTokSongRate, youtubeLink, youtubeFollowerCount, youtubeShortsBrandRate
  ]);

  // Form validation
  const isFormValid = useMemo(() => {
    return !!(
      creatorName &&
      creatorRegion &&
      contactEmail &&
      paymentEmail &&
      phoneNumber &&
      phoneNumber.length >= 11
    );
  }, [creatorName, creatorRegion, contactEmail, paymentEmail, phoneNumber]);

  // Completion percentage
  const completionPercentage = useMemo(() => {
    const requiredFields = ['creatorName', 'creatorRegion', 'contactEmail', 'paymentEmail', 'phoneNumber'];
    const filledRequired = requiredFields.filter(field => {
      if (field === 'phoneNumber') return phoneNumber.length >= 11;
      return formData[field];
    }).length;
    
    const optionalFields = ['notesContentStyle', 'instagramFollowerCount', 'tikTokFollowerCount', 'youtubeFollowerCount'];
    const filledOptional = optionalFields.filter(field => formData[field]).length;
    
    const basePercentage = (filledRequired / requiredFields.length) * 70;
    const bonusPercentage = (filledOptional / optionalFields.length) * 30;
    
    return Math.round(basePercentage + bonusPercentage);
  }, [formData, phoneNumber]);

  const handleEmailSameChange = useCallback((event) => {
    setEmailSame(event.target.checked);
    if (event.target.checked) {
      setPaymentEmail(contactEmail);
    } else {
      setPaymentEmail("");
    }
  }, [contactEmail]);

  const { mutate: addNewCreator, isLoading } = useMutation(
    client.creators.add,
    {
      onSuccess: useCallback((data) => {
        alert("Vendor added successfully");
        onClose();
      }, [onClose]),
      onError: useCallback((error) => {
        alert("Error saving vendor data:", error);
        onClose();
      }, [onClose]),
    }
  );

  const handleSaveCreator = useCallback(async () => {
    if (phoneNumber.length < 11) {
      alert(`This phone number is not valid`);
      return;
    }

    const formattedTikTokLink = tikTokLink ? `https://www.tiktok.com/@${tikTokLink}` : null;
    const formattedInstagramLink = instagramLink ? `https://www.instagram.com/${instagramLink}` : null;
    const formattedYouTubeLink = youtubeLink ? `https://www.youtube.com/${youtubeLink}` : null;

    let payload = null;

    try {
      payload = {
        creator: creatorName,
        tiktok: parseLocalInt(tikTokFollowerCount) || null,
        tiktok_link: formattedTikTokLink || null,
        instagram: parseLocalInt(instagramFollowerCount) || null,
        instagram_link: formattedInstagramLink || null,
        youtube: parseLocalInt(youtubeFollowerCount) || null,
        youtube_link: formattedYouTubeLink || null,
        geolocationgenderethnicity: `${location} / ${gender} / ${race}` || null,
        primary_markets: primaryMarket,
        region: creatorRegion,
        notescontent_style: notesContentStyle || null,
        rate_tt_sound: parseLocalFloat(tikTokSongRate),
        rate_tt_brand: parseLocalFloat(tikTokBrandRate),
        rate_ig_feedpost: parseLocalFloat(instagramStoryRate),
        rate_ig_reelsound: parseLocalFloat(instagramSongRate),
        rate_ig_reelbrand: parseLocalFloat(instagramBrandRate),
        rate_yt_short: parseLocalFloat(youtubeShortsBrandRate),
        contactEmail: contactEmail,
        paymentEmail: paymentEmail,
        phone_number: phoneNumber,
        pfphref: pfphref || null,
        is_vendor: true
      };
    } catch (error) {
      alert(`Error parsing this form: ${error}`);
      return;
    }

    addNewCreator(payload);
  }, [
    phoneNumber, tikTokLink, instagramLink, youtubeLink, creatorName, tikTokFollowerCount,
    instagramFollowerCount, youtubeFollowerCount, location, gender, race, primaryMarket,
    creatorRegion, notesContentStyle, tikTokSongRate, tikTokBrandRate, instagramStoryRate,
    instagramSongRate, instagramBrandRate, youtubeShortsBrandRate, contactEmail,
    paymentEmail, pfphref, addNewCreator
  ]);

  // Stable event handlers
  const handleInputChange = useCallback((field) => (e) => {
    const value = e.target.value;
    
    switch (field) {
      case "creatorName": setCreatorName(value); break;
      case "notesContentStyle": setNotesContentStyle(value); break;
      case "contactEmail": setContactEmail(value); break;
      case "paymentEmail": setPaymentEmail(value); break;
      case "phoneNumber": setPhoneNumber(value); break;
      case "instagramLink": setInstagramLink(value); break;
      case "instagramBrandRate": setInstagramBrandRate(value); break;
      case "instagramFollowerCount": setInstagramFollowerCount(value); break;
      case "instagramSongRate": setInstagramSongRate(value); break;
      case "tikTokLink": setTikTokLink(value); break;
      case "tikTokFollowerCount": setTikTokFollowerCount(value); break;
      case "tikTokBrandRate": setTikTokBrandRate(value); break;
      case "tikTokSongRate": setTikTokSongRate(value); break;
      case "youtubeLink": setYoutubeLink(value); break;
      case "youtubeFollowerCount": setYoutubeFollowerCount(value); break;
      case "youtubeShortsBrandRate": setYoutubeShortsBrandRate(value); break;
      default: break;
    }
  }, []);

  const handleSelectChange = useCallback((field) => (e) => {
    const value = e.target.value;
    
    if (field === "creatorRegion") {
      setCreatorRegion(value);
    }
  }, []);

  // Memoize form content props
  const formContentProps = useMemo(() => ({
    formData,
    handleInputChange,
    handleSelectChange,
    handleEmailSameChange,
    isMobile,
  }), [formData, handleInputChange, handleSelectChange, handleEmailSameChange, isMobile]);

  if (!open) return null;

  // Mobile View
  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen
        PaperProps={{
          elevation: 0,
          sx: { 
            borderRadius: 0,
            boxShadow: 'none',
            m: 0,
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            height: '100vh',
            maxHeight: '100vh',
          }
        }}
      >
        {/* Header with Gradient */}
        <Box 
          sx={{ 
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            boxShadow: theme.shadows[4],
            pt: 'max(16px, env(safe-area-inset-top, 0px))',
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `radial-gradient(circle at 20% 80%, ${alpha(theme.palette.secondary.main, 0.3)} 0%, transparent 70%)`,
              pointerEvents: 'none'
            }}
          />
          
          <Box sx={{ position: 'relative', zIndex: 1, p: 2, pb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
                  Vendor Registration
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Complete your profile
                </Typography>
              </Box>
              
              <IconButton 
                onClick={onClose}
                size="small"
                sx={{ 
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>

            <LinearProgress 
              variant="determinate" 
              value={completionPercentage} 
              sx={{
                height: 4,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'white',
                  borderRadius: 3,
                }
              }}
            />
          </Box>
        </Box>

        {/* Form Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            px: 2,
            py: 2,
            bgcolor: 'background.default',
          }}
        >
          <VendorFormContent {...formContentProps} />
        </Box>

        <Divider />

        {/* Action Buttons */}
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: 'background.paper',
            display: 'flex',
            gap: 1,
            paddingBottom: `calc(${APPSHELL_HEIGHT}px + 16px + env(safe-area-inset-bottom, 0px))`,
          }}
        >
          <Button 
            onClick={onClose} 
            color="inherit"
            size="medium"
            sx={{ borderRadius: 2, fontSize: '0.875rem' }}
          >
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleSaveCreator}
            variant="contained"
            disabled={!isFormValid || isLoading}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            size="medium"
            sx={{ borderRadius: 2, fontSize: '0.875rem' }}
          >
            {isLoading ? 'Submitting...' : 'Submit'}
          </Button>
        </Box>
      </Dialog>
    );
  }

  // Desktop View - Full Screen with Two Columns
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          m: 0,
          maxHeight: '100vh',
          maxWidth: '100vw',
          borderRadius: 0,
        }
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          height: 80,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          bgcolor: 'background.paper',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <BusinessIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Vendor Registration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete your vendor's profile to get started
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{
              width: 200,
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
            {completionPercentage}%
          </Typography>
          <IconButton 
            onClick={onClose}
            sx={{
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Main Content - Two Column Layout */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left Column - Preview Card */}
        <Box
          sx={{
            width: 380,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            overflowY: 'auto',
            p: 3,
          }}
        >
          <VendorPreviewCard
            creatorName={creatorName}
            creatorRegion={creatorRegion}
            contactEmail={contactEmail}
            phoneNumber={phoneNumber}
            notesContentStyle={notesContentStyle}
            instagramFollowerCount={instagramFollowerCount}
            tikTokFollowerCount={tikTokFollowerCount}
            youtubeFollowerCount={youtubeFollowerCount}
            completionPercentage={completionPercentage}
            isFormValid={isFormValid}
          />
        </Box>

        {/* Right Column - Form */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
          <VendorFormContent {...formContentProps} />

          {/* Action Buttons - Sticky at bottom */}
          <Box 
            sx={{ 
              position: 'sticky',
              bottom: 0,
              bgcolor: 'background.default',
              pt: 3,
              pb: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              mt: 4,
            }}
          >
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button 
                onClick={onClose} 
                color="inherit"
                size="large"
                sx={{ borderRadius: 2, px: 3 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCreator}
                variant="contained"
                disabled={!isFormValid || isLoading}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                size="large"
                sx={{ borderRadius: 2, px: 4 }}
              >
                {isLoading ? 'Submitting...' : 'Submit Registration'}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
});

VendorIntakeDialog.displayName = 'VendorIntakeDialog';

export default VendorIntakeDialog;